import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Table } from "antd";
import {
  getEmployeesResignations,
  getFnfSettlementApprover,
  getSalaryHoldTypes,
} from "../../../services/ResignationServices";
import { formatDateInTimezone } from "../../../utils/formatDateInTimezone";
import { FaHandshake, FaTasks, FaFileAlt } from "react-icons/fa";
import { companyID } from "../../../auth";
import FullAndFinalSettlement from "../../../views/pages/Employees/FullAndFinalSettlement";
import { parseISO } from "date-fns";
import Breadcrumbs from "../../../components/Breadcrumbs";
import Loader from "../../../components/Loader";
import ProtectedButton from "../../../Routes/Approuter/ProtectedButton";
import { PiMicrosoftExcelLogoFill } from "react-icons/pi";
import GenerateFinalDocumentModal from "../../../components/modelpopup/GenerateFinalDocumentModal";

const EmployeeFnf = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState({
    show: false,
    message: "",
  });
  const [tableLoading, setTableLoading] = useState(true);
  const [employeeResignations, setEmployeeResignations] = useState([]);
  const [fullAndFinalSettlement, setFullAndFinalSettlement] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [action, setAction] = useState(null);
  const [salaryHoldTypes, setSalaryHoldTypes] = useState([]);

  const [fnfApprover, setFnfApprover] = useState(false);
  const [generateModal, setGenerateModal] = useState(false);
  const [documentRecord, setDocumentRecord] = useState(null);
  const employeeId = localStorage.getItem("employeeID");
  const company_id = companyID();

  const fetchEmployeesResignations = async () => {
    if (company_id && employeeId) {
      try {
        const response = await getEmployeesResignations(company_id);
        const fnfEmployees =
          response.result.length > 0
            ? response.result.filter(
                (resignation) =>
                  resignation.resignation_status == 2 &&
                  resignation.employee_id != employeeId,
              )
            : [];
        setEmployeeResignations(fnfEmployees);

        if (location.state?.employeeId) {
          const emp = fnfEmployees.find(
            (e) => e.employee_id == location.state.employeeId,
          );
          if (emp) {
            setFullAndFinalSettlement(true);
            setAction("fnfSettlement");
            setSelectedEmployee(emp);
          }
        }
      } catch (err) {
        console.error(err);
      }
    }
  };

  const fetchSalaryHoldTypes = async () => {
    try {
      const response = await getSalaryHoldTypes();
      setSalaryHoldTypes(response.result);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchFnfSettlementApprover = async (companyId, employeeId) => {
    try {
      const response = await getFnfSettlementApprover(companyId, employeeId);
      if (response && response.fnfApprover == true) {
        setFnfApprover(true);
      } else {
        setFnfApprover(false);
      }
    } catch (error) {
      console.error("Error fetching FNF settlement approver:", error);
      setFnfApprover(false);
    }
  };

  useEffect(() => {
    fetchEmployeesResignations();
    setAction(null);
    fetchSalaryHoldTypes();
    if (company_id || employeeId) {
      fetchFnfSettlementApprover(company_id, employeeId);
    }
  }, [company_id, employeeId]);

  const handleSettlement = (record, action) => {
    setFullAndFinalSettlement(true);
    setAction(action);
    setSelectedEmployee(record);
  };

  const handleGenerateDocuments = (record) => {
    setDocumentRecord(record);
    setGenerateModal(true);
  };

  const iconButtonClass = "border-0 bg-transparent p-0 shadow-none";

  const columns = [
    {
      title: "Employee Name",
      dataIndex: "employee_name",
      render: (text) => <span className="table-avatar">{text}</span>,
    },
    {
      title: "Separation date",
      dataIndex: "resignation_date",
      key: "resignation_date",
      render: (text) => <span>{formatDateInTimezone(text)}</span>,
    },
    {
      title: "Last working date",
      dataIndex: "last_working_date",
      key: "last_working_date",
      render: (text) => <span>{formatDateInTimezone(text)}</span>,
    },
    {
      title: "Salary release date",
      dataIndex: "salary_release_date",
      key: "salary_release_date",
      render: (text) => <span>{formatDateInTimezone(text)}</span>,
    },
    {
      title: "Separation status",
      dataIndex: "resignation_status",
      key: "resignation_status",
      render: (text) => (
        <span
          className={`badge bg-${
            text == 1 ? "warning" : text == 2 ? "success" : "danger"
          }`}
        >
          {text == 1 ? "Pending" : text == 2 ? "Approved" : "Rejected"}
        </span>
      ),
    },
    {
      title: "FNF approval status",
      dataIndex: "fnf_approval_status",
      key: "fnf_approval_status",
      render: (text) => (
        <span
          className={`badge bg-${
            text == 1 || text == null
              ? "warning"
              : text == 2
                ? "success"
                : "danger"
          }`}
        >
          {text == 1 || text == null
            ? "Pending"
            : text == 2
              ? "Approved"
              : "Rejected"}
        </span>
      ),
    },
    {
      title: "Document status",
      dataIndex: "is_final_document_generated",
      render: (text, record) =>
        record?.final_document_enabled != 1 ? (
          <span className="text-muted">-</span>
        ) : (
          <span className={`badge bg-${text == 1 ? "success" : "secondary"}`}>
            {text == 1 ? "Generated" : "Not Generated"}
          </span>
        ),
    },
    {
      title: "Action",
      render: (text, record) => {
        const eligibleForSettlement =
          record?.resignation_status === 2 &&
          parseISO(record?.last_working_date)?.setHours(0, 0, 0, 0) <
            new Date()?.setHours(0, 0, 0, 0);
        const verificationPending = record?.fnf_approval_status;
        return (
          <div className="d-flex gap-3 mt-2">
            <ProtectedButton page="employee-fnf" action="create">
              <button
                disabled={eligibleForSettlement ? false : true}
                className={iconButtonClass}
                onClick={() => handleSettlement(record, "fnfSettlement")}
              >
                <FaHandshake
                  className={`${eligibleForSettlement ? "text-primary" : "text-muted"}`}
                  size={18}
                  title={
                    (record?.fnf_approval_status === 2 ? "View " : "Final ") +
                    "Settlement"
                  }
                />
              </button>
            </ProtectedButton>

            {fnfApprover && (
              <button
                disabled={
                  eligibleForSettlement && verificationPending ? false : true
                }
                className={iconButtonClass}
                onClick={() => handleSettlement(record, "verifyFNFSettlement")}
              >
                <FaTasks
                  className={`${
                    eligibleForSettlement && verificationPending
                      ? "text-primary"
                      : "text-muted"
                  }`}
                  size={18}
                  title={
                    (record?.fnf_approval_status === 2 ? "View " : "Verify ") +
                    "FNF Settlement"
                  }
                />
              </button>
            )}

            {record?.final_document_enabled == 1 &&
              record?.is_final_document_generated != 1 &&
              record?.fnf_approval_status === 2 && (
                <button
                  className={iconButtonClass}
                  onClick={() => handleGenerateDocuments(record)}
                >
                  <FaFileAlt
                    className="text-primary"
                    size={18}
                    title="Generate Document"
                  />
                </button>
              )}

            {/* <button
              disabled={record?.fnf_approval_status === 2 ? false : true}
              className={`${
                isModalVisible ? `btn btn-outline-success` : "dropdown-item"
              }`}
            >
              {isModalVisible ? (
                "Export excel"
              ) : (
                <PiMicrosoftExcelLogoFill
                  className={`${
                    record?.fnf_approval_status === 2
                      ? "text-primary"
                      : "text-muted"
                  }`}
                  title="Export fnf excel"
                  size={18}
                />
              )}
            </button> */}
          </div>
        );
      },
    },
  ];
  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <Breadcrumbs
          maintitle={
            action
              ? `${
                  action === "verifyFNFSettlement" ? "Verify " : ""
                }Full and Final Settlement Of ${selectedEmployee.employee_name}`
              : "Employees FNF"
          }
          title={action ? "Employees FNF" : "Dashboard"}
          subtitle={action ? "Full and Final Settlement" : "Employees FNF"}
          titleLink={action ? "employees-resignation" : null}
          onBackButton={() => {
            action ? navigate("../employee-fnf") : navigate(-1);
          }}
        />
        {!fullAndFinalSettlement ? (
          <div className="row">
            <div className="col-md-12">
              <div className="table-responsive">
                <Table
                  scroll={{ x: 1000 }}
                  className="table-striped"
                  columns={columns}
                  dataSource={employeeResignations?.reverse()}
                  rowKey={(record) => record.id}
                  loading={loading.show}
                />
              </div>
            </div>
          </div>
        ) : (
          <FullAndFinalSettlement
            {...{
              selectedEmployee: selectedEmployee.employee_id,
              setFullAndFinalSettlement,
              action,
              fetchEmployeesResignations,
              fnfApprover,
            }}
          />
        )}
      </div>
      <GenerateFinalDocumentModal
        show={generateModal}
        handleClose={() => {
          setGenerateModal(false);
          fetchEmployeesResignations();
        }}
        record={documentRecord}
      />
      <Loader show={loading.show} loadingMessage={loading.message} />
    </div>
  );
};

export default EmployeeFnf;
